/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / ORDERS-MODAL-STATE — Anker-Modul für Modal-Koordination
   =============================================================================
   S67 Refactor: Cross-Module-Wrapper für Modal-Aufrufe zwischen den drei
   orders.*-Modulen (orders.js, orders-correction.js, orders-receipt-actions.js).

   Hintergrund / Architekturentscheidung (Jonas-Direktive S67):
     orders.js wurde in S67 in drei Teile zerlegt, weil >2.400 LOC nicht mehr
     wartbar waren. Die zwei extrahierten Module (orders-correction.js und
     orders-receipt-actions.js) brauchen punktuell Funktionen aus dem Rest
     von orders.js (vor allem `closeOrderDetail()` aus dem Order-Detail-
     Modal-Block, der in orders.js bleibt).

     Statt einen direkten ESM-Import (`import { closeOrderDetail } from
     './orders.js'`) zu setzen — der wegen orders.js-Re-Exports der neuen
     Module einen Circular-Import-Pfad bauen würde — bündeln wir diese
     Cross-Module-Aufrufe HIER in defensiven `window.*`-Wrappern.

     Dieses Pattern ist S66-bewährt (siehe settings-delivery-rules.js,
     settings-team.js etc.).

   Warum ein eigenes Modul statt Inline-Defensive in jedem Modul?
     1. Single Source of Truth für Cross-Module-Modal-Calls — wenn sich
        die Signatur ändert (oder ein zusätzliches Modal-Hub-Schicht
        eingezogen wird), gibt es genau EINE Stelle, die anzupassen ist.
     2. Anker-Datei für künftige Modal-Coordination-Helpers (Modal-Stack,
        Focus-Trap-Manager, Escape-Key-Routing). Aktuell minimal,
        wächst mit der Pipeline.
     3. Test-Friendly: in Tests können die Wrapper gemockt werden, ohne
        window-Globals patchen zu müssen.

   API:
     callCloseOrderDetail()                   Schließt das Order-Detail-Modal
     callOpenReceiptActionModal(order, opts)  Öffnet das Receipt-Action-Modal
============================================================================ */

/**
 * Defensiver Wrapper für `closeOrderDetail()` aus orders.js.
 * Wird vor allem aus orders-correction.js nach einem submitCorrection*
 * aufgerufen, um das Order-Detail-Modal zu schließen, bevor das Korrektur-
 * Bon-Modal oder das Receipt-Action-Modal überlagert öffnet.
 *
 * No-Op wenn orders.js noch nicht geladen ist (Test-Sandbox, Bootstrap-Race).
 */
export function callCloseOrderDetail() {
  if (typeof window !== 'undefined' && typeof window.closeOrderDetail === 'function') {
    try {
      window.closeOrderDetail();
    } catch (e) {
      console.warn('[orders-modal-state] closeOrderDetail() warf:', e);
    }
  }
}

/**
 * Defensiver Wrapper für `openReceiptActionModal()` aus orders-receipt-actions.js.
 * Wird aus orders-correction.js nach einem erfolgreich erzeugten Korrektur-
 * Bon-Modal aufgerufen, um den Send/Print/Save-Aktions-Dialog für den
 * Korrektur-Beleg zu öffnen.
 *
 * No-Op wenn das Receipt-Action-Modul noch nicht geladen ist (Bootstrap-Race
 * oder Test ohne Receipt-Action-Import).
 */
export function callOpenReceiptActionModal(order, opts) {
  if (typeof window !== 'undefined' && typeof window.openReceiptActionModal === 'function') {
    try {
      window.openReceiptActionModal(order, opts);
    } catch (e) {
      console.warn('[orders-modal-state] openReceiptActionModal() warf:', e);
    }
  }
}

/**
 * Defensiver Wrapper für `openCorrectionReceiptModal()` aus orders-correction.js.
 * Wird intern aus dem Correction-Modul für die Korrektur-Bon-Vorschau
 * aufgerufen — wenn TSE-Signatur grün ODER wenn sie hart ausfällt
 * (Modal zeigt dann „Druck später wiederholen"-Hinweis).
 *
 * Im Self-Modul-Aufruf ist der direkte Import sauberer; dieser Wrapper
 * existiert als Fallback für externe Trigger (etwa aus orders.js, wenn
 * dort ein Korrektur-Bon-Reprint angeworfen wird).
 */
export function callOpenCorrectionReceiptModal(order, createdCorrections, reasonText, sigRes) {
  if (typeof window !== 'undefined' && typeof window.openCorrectionReceiptModal === 'function') {
    try {
      window.openCorrectionReceiptModal(order, createdCorrections, reasonText, sigRes);
    } catch (e) {
      console.warn('[orders-modal-state] openCorrectionReceiptModal() warf:', e);
    }
  }
}

// -----------------------------------------------------------------------------
// Backward-Compat — Inline-onclick-Handler im Modal-HTML
// (Aktuell sind alle Cross-Module-Caller bereits intern via window.* gewrappt;
// dieser Block ist Vorbereitung für künftige Verdrahtung.)
// -----------------------------------------------------------------------------

if (typeof window !== 'undefined') {
  Object.assign(window, {
    _callCloseOrderDetail: callCloseOrderDetail,
    _callOpenReceiptActionModal: callOpenReceiptActionModal,
    _callOpenCorrectionReceiptModal: callOpenCorrectionReceiptModal
  });
}
